import React, { useState, useEffect } from 'react';
import { useRegisterBusiness } from '../../api/user';
import ReCaptchaVerification from '@/components/ReCaptchaVerification';
import { Features } from '@/types/TReseller';
import BusinessDetailsStep from './steps/BusinessDetailsStep';
import ReceptionistDetailsStep from './steps/ReceptionistDetailsStep';
import QuickSetupQuestions, { QuickSetupFormValues } from './steps/QuickSetupQuestions';
import posthog from 'posthog-js';
import { getLoggedInUser } from '@/store/account/selector';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { useFeatureFlag } from 'src/Hooks/useFeatureFlag';
import { FeatureFlag } from 'src/enums/EFeatureFlags';
import { notification } from 'antd';
import { getResellerData } from '@/store/reseller/selector';
import { EBusinessLocations, EBusinessRevenue, ECallVolume } from 'src/enums/ESmbDataCollection';
import { useClarity } from 'src/Hooks/useClarity';
import { useLocation, useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { verifyCustomOffer, createCheckoutSession } from '@/api/customOffer';
import { Loading } from '@/components/Loading';
import OfferDisplay from './OfferDisplay';
import InvalidOfferDisplay from '@/components/InvalidOfferDisplay';
import LocalStorageUtility from '@/utils/localStorage';
import { LocalStorageKeys } from 'src/enums/ELocalStorage';

enum RegistrationStep {
	BUSINESS_DETAILS = 0,
	QUICK_SETUP_QUESTIONS = 1,
	RECEPTIONIST_DETAILS = 2,
}

export interface Feature {
	id: string;
	name: string;
	description: string;
	icon: string;
	benefit: string;
	setupDuration: number;
	setupOrder: number;
}

export interface RegistrationData {
	businessName?: string;
	businessType?: string;
	otherBusinessType?: string;
	hasWebsite?: string;
	websiteUrl?: string;
	firstName?: string;
	lastName?: string;
	email?: string;
	phone?: string;
	features?: Feature[];
	username?: string;
	password?: string;
	tempKey?: string;
	greetingAudio?: string;
	referralSource?: string;

	// Quick setup questions data
	callVolume?: ECallVolume;
	businessLocations?: EBusinessLocations;
	businessRevenue?: EBusinessRevenue;
	interestedFeatures: string[];
}

interface UserRegistrationPageProps {
	trialForReseller?: boolean;
	debrand?: boolean;
	handleComplete?: () => void;
	resellerId?: string;
	features?: Features;
	auto_scale?: boolean;
	resellerLogoUrl?: string;
	userId?: string;
	agentType: 'Inbound' | 'Outbound';
	resellerBusinessName?: string;
	retrieveSubmissionData?: (data: any) => void;
	fromCustomOfferAccountGeneration?: boolean;
}

const UserRegistrationPage: React.FC<UserRegistrationPageProps> = ({
	trialForReseller = false,
	debrand = false,
	handleComplete,
	resellerId,
	features,
	auto_scale,
	resellerLogoUrl,
	userId,
	agentType,
	resellerBusinessName,
	fromCustomOfferAccountGeneration = false,
	retrieveSubmissionData,
}) => {
	const location = useLocation();
	const { encodedData } = useParams<{ encodedData: string }>();
	const customOffer = location.pathname.includes('custom-offer-client');
	const loggedInUser = useAppSelector(getLoggedInUser);
	const clarity = useClarity();
	const [currentStep, setCurrentStep] = useState<RegistrationStep>(
		RegistrationStep.BUSINESS_DETAILS,
	);
	const [showCaptcha, setShowCaptcha] = useState(false);
	const [verificationTrigger, setVerificationTrigger] = useState(false);
	const [pendingRegistrationData, setPendingRegistrationData] =
		useState<Partial<RegistrationData> | null>(null);
	const [errorCount, setErrorCount] = useState(0);
	const [offerViewed, setOfferViewed] = useState(false);
	const [isStarting, setIsStarting] = useState(false);

	const resellerLogoSrc = useAppSelector(getResellerData)?.logoUrl || resellerLogoUrl;

	const isComprehensiveScrapingFlagEnabled = useFeatureFlag(FeatureFlag.COMPREHENSIVE_WEB_SCRAPING);

	const urlParams = new URLSearchParams(location.search);
	const customerName = urlParams.get('name');

	const {
		data: verifyCustomOfferData,
		isLoading: verifyCustomOfferLoading,
		error: verifyCustomOfferError,
	} = useQuery({
		queryKey: ['custom-offer-client', encodedData],
		queryFn: () => verifyCustomOffer(encodedData),
		enabled: !!encodedData && customOffer,
		retry: false,
	});

	const customOfferCheckoutSessionMutation = useMutation({
		mutationKey: ['customOfferCheckoutSession'],
		mutationFn: createCheckoutSession,
		onSuccess(data) {
			if (data.success) {
				if (data?.token) {
					LocalStorageUtility.setLocalData(LocalStorageKeys.AUTH_TOKEN, data.token);
				}
				window.location.href = data.checkoutURL;
			} else {
				notification.error({
					message: 'Error creating checkout session',
				});
			}
		},
		onError(error: any) {
			notification.error({
				message: error.message || 'Error creating checkout session',
			});
		},
	});

	const handleStartClick = () => {
		setIsStarting(true);
		setTimeout(() => {
			setOfferViewed(true);
			setIsStarting(false);
		}, 500);
	};

	const [registrationData, setRegistrationData] = useState<RegistrationData>(() => ({
		businessName: userId && loggedInUser?.businessName ? loggedInUser?.businessName : '',
		businessType: userId && loggedInUser?.businessType ? loggedInUser?.businessType : undefined,
		otherBusinessType: '',
		hasWebsite: userId && loggedInUser?.businessWebsite ? 'yes' : '',
		websiteUrl: userId && loggedInUser?.businessWebsite ? loggedInUser?.businessWebsite : '',
		username: '',
		email: '',
		password: '',
		features: [],
		tempKey: '',
		referralSource: undefined,
		callVolume: undefined,
		businessLocations: undefined,
		businessRevenue: undefined,
		interestedFeatures: [],
	}));

	const {
		mutate: registerBusiness,
		isPending,
		reset,
	} = useRegisterBusiness({
		handleError: () => {
			notification.error({
				message: 'Error',
				description: 'Could not complete the setup at this time. Please try again later.',
			});
			setCurrentStep(RegistrationStep.BUSINESS_DETAILS);
			reset();
		},
	});

	const submitRegistrationData = async (data: Partial<RegistrationData>) => {
		const regData = { ...registrationData, ...data };
		setPendingRegistrationData({
			businessName: regData.businessName,
			businessType:
				regData.businessType && regData.businessType !== 'Other'
					? regData.businessType
					: regData.otherBusinessType || loggedInUser?.businessType,
			hasWebsite: regData.hasWebsite,
			websiteUrl: regData.websiteUrl,
		});

		setRegistrationData(regData);
		setVerificationTrigger(false);
		setShowCaptcha(true);
		setTimeout(() => setVerificationTrigger(true), 1000);
	};

	const handleCaptchaComplete = (success: boolean) => {
		if (success && pendingRegistrationData) {
			setShowCaptcha(false);
			setCurrentStep(RegistrationStep.QUICK_SETUP_QUESTIONS);

			registerBusiness(pendingRegistrationData as RegistrationData, {
				onSuccess: (response) => {
					setRegistrationData((prevData) => ({
						...prevData,
						features: response.data.features,
						tempKey: response.data.temp_key,
						greetingAudio: response.data.greetingAudio,
					}));
					setPendingRegistrationData(null);
				},
				onError: (_) => {
					setCurrentStep(RegistrationStep.BUSINESS_DETAILS);
					setShowCaptcha(true);
					setVerificationTrigger(false);
					setTimeout(() => setVerificationTrigger(true), 1000);
				},
			});
		} else if (!success) {
			setVerificationTrigger(false);
			setTimeout(() => setVerificationTrigger(true), 1000);
		}
	};

	const handleRetryVerification = () => {
		setVerificationTrigger(false);
		setTimeout(() => setVerificationTrigger(true), 1000);
	};

	const handleQuickSetupSubmit = (quickSetupData: QuickSetupFormValues) => {
		setRegistrationData((prevData) => ({
			...prevData,
			...quickSetupData,
		}));

		setCurrentStep(RegistrationStep.RECEPTIONIST_DETAILS);
	};

	const handleError = async () => {
		if (errorCount < 2) {
			setCurrentStep(RegistrationStep.BUSINESS_DETAILS);
			setErrorCount((prev) => prev + 1);
		} else {
			await new Promise((resolve) => setTimeout(resolve, 2000));
			notification.error({
				message: 'Error',
				description: 'Could not complete the setup at this time. Please try again later.',
			});
		}
	};

	// Auto submit business details if loggedInUser exists
	useEffect(() => {
		if (userId && currentStep === RegistrationStep.BUSINESS_DETAILS) {
			const userData = {
				businessName: loggedInUser.businessName,
				businessType: loggedInUser.businessType,
				hasWebsite: loggedInUser.businessWebsite ? 'yes' : 'no',
				websiteUrl: loggedInUser.businessWebsite || '',
			};
			submitRegistrationData(userData);
		}
	}, [loggedInUser, currentStep, userId]);

	// Effect to handle reCAPTCHA script loading
	useEffect(() => {
		const RECAPTCHA_SITE_KEY = import.meta.env.VITE_APP_RECAPTCHA_SITE_KEY as string;
		if (!document.querySelector('script[src*="recaptcha"]')) {
			const script = document.createElement('script');
			script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
			script.async = true;
			document.head.appendChild(script);
		}

		posthog.capture('started-registration-v5');
		clarity('event', 'started-registration-v5');
	}, []);

	const handleCustomOfferCheckout = (userData: any) => {
		customOfferCheckoutSessionMutation.mutate({
			encodedData,
			userData: {
				username: userData.username.toLowerCase(),
				emailAddress: userData.email.toLowerCase(),
				businessName: userData.businessName || 'na',
				businessWebsite: userData.businessWebsite || 'na',
				password: userData.password || '',
				type: userData.type || 'Client',
				businessType: userData.businessType || 'Not Applicable',
				otherBusinessType: userData.otherBusinessType || 'Not Applicable',
				callVolume: userData.callVolume,
				businessLocations: userData.businessLocations,
				businessRevenue: userData.businessRevenue,
				interestedFeatures: userData.interestedFeatures,
				referralSource: userData.referralSource,
				trial: userData.trial,
				resellerId: userData.resellerId,
				subAccountType: userData.subAccountType,
				phoneNumber: userData.phoneNumber,
				agent: userData.agent,
			},
		});
	};

	// Show loading while verifying custom offer
	if (verifyCustomOfferLoading) {
		return <Loading />;
	}

	// Show error if custom offer is invalid
	if (
		customOffer &&
		!verifyCustomOfferLoading &&
		((verifyCustomOfferData && !verifyCustomOfferData.success) || verifyCustomOfferError)
	) {
		return <InvalidOfferDisplay />;
	}

	// Show offer display if custom offer is valid and not yet viewed
	if (customOffer && !verifyCustomOfferLoading && verifyCustomOfferData?.success && !offerViewed) {
		return (
			<OfferDisplay
				offerData={verifyCustomOfferData.data}
				onStartClick={handleStartClick}
				isLoading={isStarting}
				customerName={customerName}
			/>
		);
	}

	if (showCaptcha) {
		return (
			<div className="flex items-center justify-center min-h-screen bg-gray-50">
				<div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full">
					<h3 className="text-xl font-bold text-gray-800 mb-6 text-center">
						Human Verification Required
					</h3>
					<ReCaptchaVerification
						trigger={verificationTrigger}
						onVerificationComplete={handleCaptchaComplete}
					/>
					<button
						onClick={handleRetryVerification}
						className="mt-4 w-full px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 focus:outline-none"
					>
						Click here if verification is stuck
					</button>
				</div>
			</div>
		);
	}

	switch (currentStep) {
		case RegistrationStep.BUSINESS_DETAILS:
			return (
				<BusinessDetailsStep
					onSubmit={submitRegistrationData}
					debrand={debrand}
					resellerLogoUrl={resellerLogoSrc}
					registrationData={registrationData}
					setRegistrationData={setRegistrationData}
					resellerBusinessName={resellerBusinessName}
					customOffer={customOffer}
					fromCustomOfferAccountGeneration={fromCustomOfferAccountGeneration}
				/>
			);
		case RegistrationStep.QUICK_SETUP_QUESTIONS:
			return (
				<QuickSetupQuestions
					businessName={loggedInUser?.businessName || registrationData.businessName}
					onSubmit={handleQuickSetupSubmit}
					isAnalyzing={isPending}
					features={registrationData.features || []}
					debrand={debrand}
					tempKey={registrationData.tempKey!}
					resellerLogoUrl={resellerLogoSrc}
					isComprehensiveScrape={Boolean(
						isComprehensiveScrapingFlagEnabled &&
							(loggedInUser?.businessWebsite || registrationData.websiteUrl || ''),
					)}
					handleError={handleError}
					loggedInUser={loggedInUser}
					userId={userId}
				/>
			);
		case RegistrationStep.RECEPTIONIST_DETAILS:
			return (
				<ReceptionistDetailsStep
					businessName={
						userId && loggedInUser?.businessName
							? loggedInUser?.businessName
							: registrationData.businessName || ''
					}
					websiteUrl={
						userId && loggedInUser?.businessWebsite
							? loggedInUser?.businessWebsite
							: registrationData.websiteUrl || ''
					}
					trialForReseller={trialForReseller}
					debrand={debrand}
					handleComplete={handleComplete}
					resellerId={resellerId}
					resellerFeatures={features}
					auto_scale={auto_scale}
					tempKey={registrationData.tempKey!}
					userId={userId}
					agentType={agentType}
					businessType={registrationData.businessType}
					greetingAudio={registrationData.greetingAudio}
					callVolume={loggedInUser?.callVolume || registrationData.callVolume}
					businessLocations={loggedInUser?.businessLocations || registrationData.businessLocations}
					businessRevenue={loggedInUser?.businessRevenue || registrationData.businessRevenue}
					interestedFeatures={
						loggedInUser?.interestedFeatures || registrationData.interestedFeatures
					}
					resellerLogoUrl={resellerLogoSrc}
					referralSource={registrationData.referralSource}
					customOffer={customOffer}
					onCustomOfferCheckout={handleCustomOfferCheckout}
					isCustomOfferCheckoutLoading={customOfferCheckoutSessionMutation.isPending}
					fromCustomOfferAccountGeneration={fromCustomOfferAccountGeneration}
					retrieveSubmissionData={retrieveSubmissionData}
				/>
			);

		default:
			return null;
	}
};

export default UserRegistrationPage;
